import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';

export type ContributorRole = 'admin' | 'owner' | 'author' | 'reviewer';

export interface SpaceContributor {
	accountId: string;
	displayName: string;
	avatarUrl: string | undefined;
	jobTitle: string | undefined;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- not browser location
	location: string | undefined;
	isDeactivated: boolean;
	roles: ContributorRole[];
	created: number;
	updated: number;
	commentCreated: number;
	viewed: number;
}

// Max role labels shown per contributor.
const MAX_ROLES = 2;

interface ActivityByUserEntry {
	accountId: string;
	created: number;
	updated: number;
	commentCreated: number;
	viewed: number;
}

interface ActivityByUserResponse {
	experimentalAnalyticsActivityByUser: {
		activityByUser: ActivityByUserEntry[];
	};
}

// AGG batch user query for resolving display names and avatars
const FetchUsersBatchQuery = gql`
	query useSpaceContributorsFetchUsersBatchQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
			accountStatus
		}
	}
`;

interface AGGUser {
	accountId: string;
	name: string | null;
	picture: string | null;
	accountStatus: 'active' | 'inactive' | 'closed';
}

interface FetchUsersBatchData {
	users?: (AGGUser | null)[] | null;
}

interface UserProfile {
	displayName: string;
	avatarUrl: string | undefined;
	jobTitle: string | undefined;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- not browser location
	location: string | undefined;
	isDeactivated: boolean;
}

const AGG_USERS_BATCH_LIMIT = 90;

const normalizeLocation = (location: string | null | undefined): string | undefined => {
	if (!location) {
		return undefined;
	}
	return location.replace(/^Home office:\s*/i, '').trim() || undefined;
};

const MIN_CREATED_THRESHOLD = 5;
const MIN_UPDATED_THRESHOLD = 10;
const MIN_COMMENT_THRESHOLD = 10;

const hasSignificantActivity = (entry: ActivityByUserEntry): boolean =>
	(entry.created ?? 0) >= MIN_CREATED_THRESHOLD ||
	(entry.updated ?? 0) >= MIN_UPDATED_THRESHOLD ||
	(entry.commentCreated ?? 0) >= MIN_COMMENT_THRESHOLD;

interface SpaceRoles {
	adminIds: Set<string>;
	ownerId: string | undefined;
}

// Roles in display priority order (owner/admin, then author, then reviewer),
// capped at MAX_ROLES. Pushing in priority order keeps the result pre-sorted.
export const computeRoles = (
	entry: ActivityByUserEntry,
	spaceRoles: SpaceRoles,
): ContributorRole[] => {
	const roles: ContributorRole[] = [];
	// Owner takes precedence over admin.
	if (spaceRoles.ownerId === entry.accountId) {
		roles.push('owner');
	} else if (spaceRoles.adminIds.has(entry.accountId)) {
		roles.push('admin');
	}
	if ((entry.created ?? 0) >= MIN_CREATED_THRESHOLD) {
		roles.push('author');
	}
	if (
		(entry.updated ?? 0) >= MIN_UPDATED_THRESHOLD ||
		(entry.commentCreated ?? 0) >= MIN_COMMENT_THRESHOLD
	) {
		roles.push('reviewer');
	}
	return roles.slice(0, MAX_ROLES);
};

const compareContributors = (a: ActivityByUserEntry, b: ActivityByUserEntry): number => {
	// Pages created is the primary sort (more superior)
	const createdDiff = (b.created ?? 0) - (a.created ?? 0);
	if (createdDiff !== 0) {
		return createdDiff;
	}
	// Edits (updated) as secondary sort
	const updatedDiff = (b.updated ?? 0) - (a.updated ?? 0);
	if (updatedDiff !== 0) {
		return updatedDiff;
	}
	// Comments as tertiary sort
	return (b.commentCreated ?? 0) - (a.commentCreated ?? 0);
};

const fetchUserProfiles = async (
	accountIds: string[],
	signal: AbortSignal,
): Promise<Map<string, UserProfile>> => {
	const profileMap = new Map<string, UserProfile>();

	if (accountIds.length === 0 || signal.aborted) {
		return profileMap;
	}

	const batches: string[][] = [];
	for (let i = 0; i < accountIds.length; i += AGG_USERS_BATCH_LIMIT) {
		batches.push(accountIds.slice(i, i + AGG_USERS_BATCH_LIMIT));
	}

	const processBatch = async (batch: string[]) => {
		if (signal.aborted) {
			return;
		}

		try {
			const { data, error } = await queryInterop<FetchUsersBatchData, { accountIds: string[] }>({
				query: FetchUsersBatchQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: { accountIds: batch },
				interopApolloAggClient: true,
			});

			if (error?.graphQLErrors) {
				error.graphQLErrors.forEach(markErrorAsHandled);
			}

			const users = data?.users ?? [];
			for (const user of users) {
				if (user !== null) {
					profileMap.set(user.accountId, {
						displayName: user.name ?? user.accountId,
						avatarUrl: user.picture ?? undefined,
						jobTitle: undefined,
						location: undefined,
						isDeactivated: user.accountStatus !== 'active',
					});
				}
			}
		} catch {
			// Silently handle batch failures
		}
	};

	await Promise.all(batches.map(processBatch));

	return profileMap;
};

interface AtlassianUserProfile {
	title: string | null;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- not browser location
	location: string | null;
}

const buildAliasedUserQuery = (accountIds: string[]): string => {
	const fields = accountIds
		.map((id, index) => `user_${index}: confluence_atlassianUser(id: "${id}") { title location }`)
		.join('\n');
	return `query useSpaceContributorsProfileDetailsQuery { ${fields} }`;
};

const ALIASED_BATCH_LIMIT = 25;

const fetchUserProfileDetails = async (
	accountIds: string[],
	signal: AbortSignal,
): Promise<Map<string, AtlassianUserProfile>> => {
	const detailsMap = new Map<string, AtlassianUserProfile>();

	if (accountIds.length === 0 || signal.aborted) {
		return detailsMap;
	}

	const batches: string[][] = [];
	for (let i = 0; i < accountIds.length; i += ALIASED_BATCH_LIMIT) {
		batches.push(accountIds.slice(i, i + ALIASED_BATCH_LIMIT));
	}

	const processBatch = async (batch: string[]) => {
		if (signal.aborted) {
			return;
		}

		try {
			const queryString = buildAliasedUserQuery(batch);
			const { data, error } = await queryInterop<
				Record<string, AtlassianUserProfile | null>,
				Record<string, never>
			>({
				query: gql(queryString) as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: {},
				interopApolloAggClient: true,
			});

			if (error?.graphQLErrors) {
				error.graphQLErrors.forEach(markErrorAsHandled);
			}

			if (data) {
				batch.forEach((accountId, index) => {
					const user = data[`user_${index}`];
					if (user) {
						detailsMap.set(accountId, {
							title: user.title,
							location: user.location,
						});
					}
				});
			}
		} catch {
			// Silently handle batch failures
		}
	};

	await Promise.all(batches.map(processBatch));

	return detailsMap;
};

const FetchSpaceRolesQuery = gql`
	query useSpaceContributorsSpaceRolesQuery($spaceId: ID!, $first: Int) {
		space(id: $spaceId) {
			id
			spaceOwner {
				ownerId
				ownerType
			}
			spaceAdmins(first: $first, excludeAddOns: true) {
				nodes {
					__typename
					... on KnownUser {
						accountId
					}
				}
			}
		}
	}
`;

interface SpaceRolesResponse {
	space?: {
		spaceOwner?: {
			ownerId: string | null;
			ownerType: 'USER' | 'GROUP' | null;
		} | null;
		spaceAdmins?: {
			nodes?: ({ __typename: string; accountId?: string | null } | null)[] | null;
		} | null;
	} | null;
}

const SPACE_ADMINS_LIMIT = 50;

const fetchSpaceRoles = async (spaceId: string, signal: AbortSignal): Promise<SpaceRoles> => {
	const adminIds = new Set<string>();
	let ownerId: string | undefined;

	if (!spaceId || signal.aborted) {
		return { adminIds, ownerId };
	}

	try {
		const { data, error } = await queryInterop<
			SpaceRolesResponse,
			{ spaceId: string; first: number }
		>({
			query: FetchSpaceRolesQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
			variables: { spaceId, first: SPACE_ADMINS_LIMIT },
			interopApolloAggClient: true,
		});

		if (error?.graphQLErrors) {
			error.graphQLErrors.forEach(markErrorAsHandled);
		}

		for (const node of data?.space?.spaceAdmins?.nodes ?? []) {
			if (node?.accountId) {
				adminIds.add(node.accountId);
			}
		}

		const owner = data?.space?.spaceOwner;
		if (owner?.ownerType === 'USER' && owner.ownerId) {
			ownerId = owner.ownerId;
		}
	} catch (error) {
		// Roles are additive; report the failure but still render contributors.
		getMonitoringClient().submitError(error, { attribution: Attribution.DISCO });
	}

	return { adminIds, ownerId };
};

interface UseSpaceContributorsResult {
	contributors: SpaceContributor[];
	loading: boolean;
	error: Error | null;
}

const TODAY = new Date();
const SIX_MONTHS_AGO = new Date(TODAY);
SIX_MONTHS_AGO.setMonth(TODAY.getMonth() - 6);
const FROM_DATE = SIX_MONTHS_AGO.toISOString().split('T')[0];
const TO_DATE = TODAY.toISOString().split('T')[0];
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EMPTY_VARIABLES: Record<string, string> = {};

const parseActivityResult = (json: unknown) =>
	({ experimentalAnalyticsActivityByUser: json }) as ActivityByUserResponse;

export const useSpaceContributors = (spaceKey: string): UseSpaceContributorsResult => {
	const { cloudId } = useSessionData();
	const spaceId = useSpaceId(spaceKey);
	const [contributors, setContributors] = useState<SpaceContributor[]>([]);
	const [profilesLoading, setProfilesLoading] = useState(true);
	const [profilesError, setProfilesError] = useState<Error | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const url = useMemo(() => {
		if (!cloudId || !spaceId) {
			return '';
		}

		const params = new URLSearchParams({
			fromDate: FROM_DATE,
			toDate: TO_DATE,
			timezone: TIMEZONE,
			content: 'page,livedoc',
			spaceId,
			sortBy: 'created',
			sortOrder: 'desc',
			limit: '50',
		});

		return `/gateway/api/ex/confluence/${cloudId}/analytics/v2/activityByUser?${params.toString()}`;
	}, [cloudId, spaceId]);

	const {
		data,
		loading: activityLoading,
		error,
	} = useExperimentalQuery<ActivityByUserResponse, Record<string, string>, Error>({
		url,
		method: 'GET',
		credentials: 'include',
		headers: { 'Content-Type': MediaType.JSON },
		parseResult: parseActivityResult,
		skip: !cloudId || !spaceId,
		variables: EMPTY_VARIABLES,
	});

	const activeEntries = useMemo(
		() =>
			(data?.experimentalAnalyticsActivityByUser?.activityByUser ?? [])
				.filter(hasSignificantActivity)
				.sort(compareContributors),
		[data],
	);

	const resolveProfiles = useCallback(
		async (entries: ActivityByUserEntry[], resolvedSpaceId: string) => {
			// Cancel any in-flight profile fetch
			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			setProfilesLoading(true);
			setProfilesError(null);
			try {
				// Roles and the activity-entry profiles have no interdependency, so
				// fetch them concurrently rather than waterfalling.
				const entryIds = entries.map((entry) => entry.accountId);
				const [spaceRoles, profileMap, detailsMap] = await Promise.all([
					fetchSpaceRoles(resolvedSpaceId, controller.signal),
					fetchUserProfiles(entryIds, controller.signal),
					fetchUserProfileDetails(entryIds, controller.signal),
				]);

				// Account ids with a space-level role.
				const roleIds = new Set<string>(spaceRoles.adminIds);
				if (spaceRoles.ownerId) {
					roleIds.add(spaceRoles.ownerId);
				}

				// Owners/admins not already among the activity entries still need
				// their profiles resolved.
				const fetchedIds = new Set(entryIds);
				const extraIds = Array.from(roleIds).filter((id) => !fetchedIds.has(id));
				if (extraIds.length > 0 && !controller.signal.aborted) {
					const [extraProfiles, extraDetails] = await Promise.all([
						fetchUserProfiles(extraIds, controller.signal),
						fetchUserProfileDetails(extraIds, controller.signal),
					]);
					extraProfiles.forEach((profile, id) => profileMap.set(id, profile));
					extraDetails.forEach((detail, id) => detailsMap.set(id, detail));
				}

				// Merge activity entries with owners/admins, deduping by accountId.
				// Owners/admins without activity are added with zero counts.
				const mergedEntries = new Map<string, ActivityByUserEntry>();
				for (const entry of entries) {
					mergedEntries.set(entry.accountId, entry);
				}
				for (const accountId of roleIds) {
					if (!mergedEntries.has(accountId)) {
						mergedEntries.set(accountId, {
							accountId,
							created: 0,
							updated: 0,
							commentCreated: 0,
							viewed: 0,
						});
					}
				}
				const dedupedEntries = Array.from(mergedEntries.values()).sort(compareContributors);

				if (!controller.signal.aborted) {
					const resolved: SpaceContributor[] = dedupedEntries.map((entry) => {
						const profile = profileMap.get(entry.accountId);
						const details = detailsMap.get(entry.accountId);
						return {
							...entry,
							displayName: profile?.displayName ?? entry.accountId,
							avatarUrl: profile?.avatarUrl,
							jobTitle: details?.title ?? undefined,
							location: normalizeLocation(details?.location),
							isDeactivated: profile?.isDeactivated ?? false,
							roles: computeRoles(entry, spaceRoles),
						};
					});

					setContributors(resolved);
				}
			} catch (err) {
				if (!controller.signal.aborted) {
					setProfilesError(
						err instanceof Error ? err : new Error('Failed to load contributor profiles'),
					);
				}
			} finally {
				setProfilesLoading(false);
			}
		},
		[],
	);

	useEffect(() => {
		if (activityLoading) {
			return;
		}

		if (spaceId) {
			void resolveProfiles(activeEntries, spaceId);
		} else {
			setContributors([]);
			setProfilesLoading(false);
		}

		return () => {
			abortControllerRef.current?.abort();
		};
	}, [activeEntries, resolveProfiles, activityLoading, spaceId]);

	return {
		contributors,
		loading: activityLoading || profilesLoading,
		error: error ?? profilesError,
	};
};
